import React from "react";
import { motion } from "framer-motion";
import { Star, Loader2, AlertCircle, X } from "lucide-react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";

/**
 * Two-way rating modal used by Customer (rate-driver after confirm-complete) and
 * Driver (rate-customer after mark-ready). Props:
 *   - endpoint: e.g. "/customer/jobs/{id}/rate-driver" (full path after /api)
 *   - title: e.g. "How was your driver?"
 *   - subtitle: e.g. "Your rating helps other customers pick the right driver."
 *   - onClose, onRated (optional callback after submission)
 *   - optional: optional (if true, "Skip" button shows). Default false.
 */
export default function RatingModal({ endpoint, title, subtitle, ratee_name, onClose, onRated, optional = false }) {
  const [stars, setStars] = React.useState(0);
  const [hover, setHover] = React.useState(0);
  const [comment, setComment] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const submit = async () => {
    if (stars < 1) {
      setError("Pick at least 1 star.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await apiClient.post(endpoint, { stars, comment: comment.trim() || null });
      if (onRated) await onRated(stars);
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't submit rating");
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="rating-modal"
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={optional ? onClose : undefined}
    >
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="bg-white border border-[#0A0A0A]/10 max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Rating</p>
            <h3 className="font-display mt-2 text-2xl font-bold tracking-tight text-[#0A0A0A]">{title}</h3>
            {subtitle && <p className="mt-2 text-sm text-[#475569]">{subtitle}</p>}
            {ratee_name && <p className="mt-3 text-sm text-[#0A0A0A] font-semibold">{ratee_name}</p>}
          </div>
          {optional && (
            <button onClick={onClose} data-testid="rating-modal-close" className="p-2 hover:bg-[#F5F5F5]"><X size={18} /></button>
          )}
        </div>

        {/* Stars row */}
        <div className="my-6 flex items-center justify-center gap-2" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map((n) => {
            const active = (hover || stars) >= n;
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setStars(n)}
                data-testid={`rating-star-${n}`}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star
                  size={36}
                  className={active ? "text-[#F5A623]" : "text-[#0A0A0A]/15"}
                  fill={active ? "#F5A623" : "none"}
                />
              </button>
            );
          })}
        </div>

        <textarea
          rows={3}
          maxLength={500}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add a comment (optional, max 500 chars)"
          data-testid="rating-comment"
          className="w-full px-3 py-2.5 bg-white border border-[#0A0A0A]/15 focus:outline-none focus:border-[#1E4D55] focus:ring-2 focus:ring-[#1E4D55]/15 text-sm"
        />

        {error && (
          <div data-testid="rating-error" className="mt-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-3">
          {optional ? (
            <button type="button" onClick={onClose} data-testid="rating-skip" className="text-xs font-semibold text-[#475569] hover:text-[#0A0A0A]">
              Skip for now
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || stars < 1}
            data-testid="rating-submit"
            className="inline-flex items-center gap-2 bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {submitting ? "Submitting…" : "Submit rating"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
